import _objectSpread from "@babel/runtime/helpers/objectSpread";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _excluded = ["type"];
import _regeneratorRuntime from "@babel/runtime/regenerator";
import { useCallback, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useLazyQuery } from '@apollo/client';
import { useRootComponents } from '../../context/rootComponents';
import mergeOperations from '../../util/shallowMerge';
import { getComponentData } from '../../util/magentoRouteData';
import { useAppContext } from '../../context/app';
import { getRootComponent, isRedirect } from './helpers';
import DEFAULT_OPERATIONS from './magentoRoute.gql';

var getInlinedPageData = function getInlinedPageData() {
  return globalThis.INLINED_PAGE_TYPE && globalThis.INLINED_PAGE_TYPE.type ? globalThis.INLINED_PAGE_TYPE : null;
};

var resetInlinedPageData = function resetInlinedPageData() {
  globalThis.INLINED_PAGE_TYPE = false;
};

export var useMagentoRoute = function useMagentoRoute() {
  var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var operations = mergeOperations(DEFAULT_OPERATIONS, props.operations);
  var resolveUrlQuery = operations.resolveUrlQuery;

  var _useHistory = useHistory(),
      replace = _useHistory.replace;

  var _useLocation = useLocation(),
      pathname = _useLocation.pathname;

  var _useRootComponents = useRootComponents(),
      _useRootComponents2 = _slicedToArray(_useRootComponents, 2),
      componentMap = _useRootComponents2[0],
      setComponentMap = _useRootComponents2[1];

  var initialized = useRef(false);
  var fetchedPathname = useRef(null);

  var _useAppContext = useAppContext(),
      _useAppContext2 = _slicedToArray(_useAppContext, 2),
      appState = _useAppContext2[0],
      appApi = _useAppContext2[1];

  var appActions = appApi.actions;
  var nextRootComponent = appState.nextRootComponent;
  var setNextRootComponent = appActions.setNextRootComponent,
      setPageLoading = appActions.setPageLoading;
  var setComponent = useCallback(function (key, value) {
    setComponentMap(function (prevMap) {
      return new Map(prevMap).set(key, value);
    });
  }, [setComponentMap]);
  var component = componentMap.get(pathname);

  var _useLazyQuery = useLazyQuery(resolveUrlQuery),
      _useLazyQuery2 = _slicedToArray(_useLazyQuery, 2),
      runQuery = _useLazyQuery2[0],
      queryResult = _useLazyQuery2[1]; // destructure the query result


  var data = queryResult.data,
      error = queryResult.error,
      loading = queryResult.loading;

  var _ref = data || {},
      route = _ref.route;

  useEffect(function () {
    if (initialized.current || !getInlinedPageData()) {
      runQuery({
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
        variables: {
          url: pathname
        }
      });
      fetchedPathname.current = pathname;
    }
  }, [initialized, pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(function () {
    if (component) {
      return;
    }

    _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var _ref3, type, routeData, _ref4, id, identifier, uid, isEmpty, rootComponent;

      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              _ref3 = route || {}, type = _ref3.type, routeData = _objectWithoutProperties(_ref3, _excluded);
              _ref4 = routeData || {}, id = _ref4.id, identifier = _ref4.identifier, uid = _ref4.uid;
              isEmpty = !id && !identifier && !uid;

              if (!(!type || isEmpty)) {
                _context.next = 5;
                break;
              }

              return _context.abrupt("return");

            case 5:
              _context.prev = 5;
              _context.next = 8;
              return getRootComponent(type);

            case 8:
              rootComponent = _context.sent;
              setComponent(pathname, _objectSpread({
                component: rootComponent
              }, getComponentData(routeData), {
                type: type
              }));
              _context.next = 16;
              break;

            case 12:
              _context.prev = 12;
              _context.t0 = _context["catch"](5);

              if (process.env.NODE_ENV !== 'production') {
                console.error(_context.t0);
              }

              setComponent(pathname, _context.t0);

            case 16:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, null, [[5, 12]]);
    }))();
  }, [route]); // eslint-disable-line react-hooks/exhaustive-deps

  var _ref5 = route || {},
      id = _ref5.id,
      identifier = _ref5.identifier,
      uid = _ref5.uid,
      redirect_code = _ref5.redirect_code,
      relative_url = _ref5.relative_url,
      type = _ref5.type; // evaluate both results and determine the response type


  var empty = !route || !type || !id && !identifier && !uid;
  var redirect = isRedirect(redirect_code);
  var fetchError = component instanceof Error && component;
  var routeError = fetchError || error;
  var isInitialized = initialized.current || !getInlinedPageData();
  var showPageLoader = false;
  var routeData;

  if (component && !fetchError) {
    // FOUND
    routeData = component;
  } else if (routeError) {
    // ERROR
    routeData = {
      hasError: true,
      routeError: routeError
    };
  } else if (empty && fetchedPathname.current === pathname && !loading) {
    // NOT FOUND
    routeData = {
      isNotFound: true
    };
  } else if (nextRootComponent) {
    // LOADING with full page shimmer
    showPageLoader = true;
    routeData = {
      isLoading: true,
      shimmer: nextRootComponent
    };
  } else if (redirect) {
    // REDIRECT
    routeData = {
      isRedirect: true,
      relativeUrl: relative_url.startsWith('/') ? relative_url : '/' + relative_url
    };
  } else {
    // LOADING
    var isInitialLoad = !isInitialized;
    routeData = {
      isLoading: true,
      initial: isInitialLoad
    };
  }

  useEffect(function () {
    _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
      var inlinedData, componentType, rootComponent;
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              inlinedData = getInlinedPageData();

              if (!inlinedData) {
                _context2.next = 13;
                break;
              }

              _context2.prev = 2;
              componentType = inlinedData.type;
              _context2.next = 6;
              return getRootComponent(componentType);

            case 6:
              rootComponent = _context2.sent;
              setComponent(pathname, _objectSpread({
                component: rootComponent,
                type: componentType
              }, getComponentData(inlinedData)));
              _context2.next = 13;
              break;

            case 10:
              _context2.prev = 10;
              _context2.t0 = _context2["catch"](2);
              setComponent(pathname, _context2.t0);

            case 13:
              initialized.current = true;

            case 14:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2, null, [[2, 10]]);
    }))();

    return function () {
      // Unmount
      resetInlinedPageData();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // perform a redirect if necesssary

  useEffect(function () {
    if (routeData && routeData.isRedirect) {
      replace(routeData.relativeUrl);
    }
  }, [pathname, replace, routeData]);
  useEffect(function () {
    if (component) {
      // Reset loading shimmer whenever component resolves
      setNextRootComponent(null);
    }
  }, [component, pathname, setNextRootComponent]);
  useEffect(function () {
    setPageLoading(showPageLoader);
  }, [showPageLoader, setPageLoading]);
  return routeData;
};